import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PricingType {
  key: string;
  label: string;
  unit_label: string;
  sort_order: number;
  is_active: boolean;
}

export function usePricingTypes(includeInactive = false) {
  return useQuery({
    queryKey: ["pricing_types", includeInactive],
    queryFn: async () => {
      let q = supabase.from("pricing_types").select("*").order("sort_order");
      if (!includeInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return data as PricingType[];
    },
  });
}

export function useCreatePricingType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (pt: Omit<PricingType, "is_active"> & { is_active?: boolean }) => {
      const { error } = await supabase.from("pricing_types").insert(pt);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing_types"] }),
  });
}

export function useUpdatePricingType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, ...updates }: Partial<PricingType> & { key: string }) => {
      const { error } = await supabase.from("pricing_types").update(updates).eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing_types"] }),
  });
}

export function useDeletePricingType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase.from("pricing_types").delete().eq("key", key);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing_types"] }),
  });
}

/** Helper to get label from pricing types array */
export function getPricingLabel(types: PricingType[] | undefined, key: string): string {
  return types?.find((t) => t.key === key)?.label ?? key;
}

/** Helper to get unit label from pricing types array */
export function getUnitLabel(types: PricingType[] | undefined, key: string): string {
  return types?.find((t) => t.key === key)?.unit_label ?? "qty";
}
